import type {
  AdminDataset,
  AdminListResponse,
  AdminRecord,
  AdminSessionResponse,
  RiderSummary,
  SampleRequest,
} from "./types";

const DEFAULT_ADMIN_API_URL = "https://api.kithub24.com/admin";

export const adminApiBaseUrl =
  import.meta.env.VITE_ADMIN_API_URL?.trim() || DEFAULT_ADMIN_API_URL;

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "AdminApiError";
  }
}

function buildUrl(path: string, query?: Record<string, string | undefined>) {
  const base = adminApiBaseUrl.replace(/\/+$/, "");
  const url = new URL(`${base}${path.startsWith("/") ? path : `/${path}`}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value && value.trim()) url.searchParams.set(key, value);
  }
  return url.toString();
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : {};
  if (!response.ok) {
    const error = data as {
      error?: { code?: string; message?: string };
      message?: string;
    };
    throw new AdminApiError(
      error.error?.message || error.message || `요청 실패 (${response.status})`,
      response.status,
      error.error?.code,
    );
  }
  return data as T;
}

async function requestJson<T>(
  path: string,
  token: string | null,
  options: {
    method?: string;
    body?: Record<string, unknown>;
    query?: Record<string, string | undefined>;
    authenticated?: boolean;
  } = {},
): Promise<T> {
  const headers = new Headers();
  if (options.body) headers.set("content-type", "application/json; charset=utf-8");
  if (options.authenticated !== false && token) {
    headers.set("authorization", `Bearer ${token}`);
  }
  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? "GET",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  return readJsonResponse<T>(response);
}

export async function createAdminSession(password: string) {
  return requestJson<AdminSessionResponse>("/v1/admin/session", null, {
    method: "POST",
    authenticated: false,
    body: { password },
  });
}

async function listItems<T>(path: string, token: string, query?: Record<string, string>) {
  const data = await requestJson<AdminListResponse<T>>(path, token, { query });
  return Array.isArray(data.items) ? data.items : [];
}

export async function loadAdminDataset(token: string): Promise<AdminDataset> {
  const [requests, riders, campaigns, products, suppliers, orders, orderClaims] =
    await Promise.all([
      listItems<SampleRequest>("/v1/admin/sample-requests", token, { status: "all" }),
      listItems<RiderSummary>("/v1/admin/riders", token),
      listItems<AdminRecord>("/v1/admin/sample-campaigns", token),
      listItems<AdminRecord>("/v1/admin/products", token),
      listItems<AdminRecord>("/v1/admin/suppliers", token),
      listItems<AdminRecord>("/v1/admin/orders", token).catch(() => []),
      listItems<AdminRecord>("/v1/admin/order-claims", token).catch(() => []),
    ]);

  return { requests, riders, campaigns, products, suppliers, orders, orderClaims };
}

export async function updateSampleRequestStatus(
  token: string,
  id: string,
  status: string,
) {
  return requestJson<{ sampleRequest: SampleRequest }>(
    `/v1/admin/sample-requests/${encodeURIComponent(id)}/status`,
    token,
    {
      method: "PATCH",
      body: { status },
    },
  );
}

export async function assignSampleRequestRider(
  token: string,
  id: string,
  rider?: RiderSummary | null,
) {
  return requestJson<{ sampleRequest: SampleRequest }>(
    `/v1/admin/sample-requests/${encodeURIComponent(id)}/rider`,
    token,
    {
      method: "PATCH",
      body: rider?.userId
        ? {
            riderUserId: rider.userId,
            riderName: rider.name,
          }
        : {},
    },
  );
}

export async function updateSampleRequestPayout(
  token: string,
  id: string,
  payoutStatus: string,
  payoutAmount?: number,
) {
  return requestJson<{ sampleRequest: SampleRequest }>(
    `/v1/admin/sample-requests/${encodeURIComponent(id)}/payout`,
    token,
    {
      method: "PATCH",
      body: {
        payoutStatus,
        ...(Number.isFinite(payoutAmount) ? { payoutAmount } : {}),
      },
    },
  );
}

export async function updateSampleCampaignStatus(
  token: string,
  id: string,
  status: string,
) {
  return requestJson<{ sampleCampaign: AdminRecord }>(
    `/v1/admin/sample-campaigns/${encodeURIComponent(id)}/status`,
    token,
    {
      method: "PATCH",
      body: { status },
    },
  );
}
